/**
 * The one channel between the 3D lanyard and the Index scroll scene.
 *
 * Its own module on purpose: Lanyard.tsx is lazy-loaded with Three.js and
 * Rapier behind it, and the scene must be able to read this without pulling
 * that whole bundle into the main chunk.
 */
export const badgeLink = {
    /** The card's on-screen box in client px, refreshed every rendered frame. Null until the card has rendered. */
    rect: null as null | { x: number; y: number; w: number; h: number },
    /** 0–1: how firmly to hold the card upright, still and facing front. */
    steady: 0,
    /** Installed by the lanyard: stop (true) or resume (false) its render loop. */
    setPaused: null as null | ((paused: boolean) => void),
};

/** The kinds of nudge the docked card answers (one per section transition). */
export type DockNudge = "push" | "zoom" | "wipe" | "fold" | "drop";

/**
 * The channel between BadgeDock (main chunk) and the docked lanyard (lazy
 * chunk): the dock queues nudges and says whether the card is open; the card
 * reports hover and clicks back.
 */
export interface DockLink {
    /** Nudges waiting for the next physics frame. */
    queue: { kind: DockNudge; dir: number }[];
    /** Open: the card turns its back (the contact side) to the reader. */
    open: boolean;
    /** Until this time (ms, performance.now) the card is steered back to face front. */
    settleUntil: number;
    /** Reported by the card: it has stopped moving, so the loop may sleep. */
    settled: boolean;
    onToggle?: () => void;
    onHover?: (hovered: boolean) => void;
}

export function createDockLink(): DockLink {
    return { queue: [], open: false, settleUntil: 0, settled: false };
}
